import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ComponentData, ComponentRenderMode } from '../../../types/component-skin';
import { 
  checkLoginStatus, 
  selectUserInfo, 
  updateUserProfile, 
  getUserById, 
  clearAdminCache, 
  checkAdminToken,
  selectIsUserLoggedIn,
  selectWithcookieData
} from '../../../redux/WithcookieSlice';
import { showToast } from '../../common/Toast';
import { useTranslation } from '../../../redux/TranslationHelper';
import { UserProfileLogicReturn, EditedUserInfo } from './UserProfile.types';

/**
 * 사용자 프로필 로직 훅
 */
export const useUserProfileLogic = (componentData: ComponentData, mode: ComponentRenderMode): UserProfileLogicReturn => {
  // 호환성을 위한 props 변환
  const props = { ...componentData.props, ...componentData.componentProps, ...componentData };
  const dispatch = useDispatch() as any;
  const { t } = useTranslation();
  const navigate = props?.navigate;
  
  // Redux 상태
  const userInfo = useSelector(selectUserInfo) as any;
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const withcookieData = useSelector(selectWithcookieData);
  const userInfoLoading = useSelector((state: any) => state.withcookie.userInfoLoading);
  const user = useSelector((state: any) => state.withcookie.user);
  
  // Props에서 필요한 값 추출
  const varFields = props?.varFields || {};
  const isAdminMode = mode === 'editor' || props?.mode === 'admin' || props?.isPreview;
  const targetUserId = props?.targetUserId;
  const isEditorMode = props?.isEditorMode || false;
  
  // 테마 색상 가져오기
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#007bff";
  const secondaryColor = colorSet?.secondary || "#6c757d";
  const successColor = colorSet?.tertiary || "#28a745";
  
  // 로컬 상태
  const [copied, setCopied] = useState(false);
  const [referralLink, setReferralLink] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedUserInfo, setEditedUserInfo] = useState<EditedUserInfo>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isReloading, setIsReloading] = useState(false);
  const [highlightedFields, setHighlightedFields] = useState<Record<string, boolean>>({});
  const [showWithdrawalDialog, setShowWithdrawalDialog] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  
  // 사용자 정보 조회 - 무한 루프 방지 개선
  useEffect(() => {
    // 에디터 모드에서는 사용자 정보 로드를 스킵
    if (isEditorMode) {
      return;
    }
    
    // 이미 사용자 정보가 있거나 로딩 중이면 스킵
    if (userInfo || userInfoLoading) {
      return;
    }
    
    // 기본 사용자 정보가 있으면 상세 정보 로드
    if (user?.id) {
      const userId = user.id;
      const companyId = user.company_id || 21;
      dispatch(getUserById({ userId, companyId }));
      setIsInitialized(true);
      return;
    }
  }, [dispatch, user, userInfo, userInfoLoading, isEditorMode]);

  // 추천링크 생성
  useEffect(() => {
    if (userInfo && userInfo.insungId) {
      const host = window.location.host;
      setReferralLink(`https://${host}/signup/${userInfo.insungId}`);
    }
  }, [userInfo]);

  // 수정 모드로 전환될 때 현재 사용자 정보로 편집 상태 초기화
  useEffect(() => {
    if (userInfo && isEditing) {
      setEditedUserInfo({
        name: userInfo.name || '',
        phone: userInfo.phone || '',
        email: userInfo.email || '',
        birthday: userInfo.birthdate || userInfo.birthday || '',
        address: userInfo.address || '',
        var01: userInfo.var01 || '',
        var02: userInfo.var02 || '',
        var03: userInfo.var03 || '',
        var04: userInfo.var04 || '',
        var05: userInfo.var05 || '',
        var06: userInfo.var06 || '',
        var07: userInfo.var07 || '',
        var08: userInfo.var08 || '',
        var09: userInfo.var09 || '',
        var10: userInfo.var10 || '',
        trxWithdrawAddress: userInfo.trxWithdrawAddress || '',
        bscWithdrawAddress: userInfo.bscWithdrawAddress || '',
      });
    }
  }, [isEditing, userInfo]);
  
  // 하이라이트 효과 초기화
  useEffect(() => {
    if (Object.keys(highlightedFields).length > 0) {
      const timer = setTimeout(() => {
        setHighlightedFields({});
      }, 3000); // 3초 후 하이라이트 효과 제거
      
      return () => clearTimeout(timer);
    }
  }, [highlightedFields]);
  
  // 클립보드에 복사 기능
  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000); // 3초 후 메시지 숨김
      })
      .catch(err => {
        console.error(t('클립보드 복사 실패:'), err);
      });
  };

  // 수정 시작
  const handleEdit = () => {
    setIsEditing(true);
    setSaveError(null);
  };

  // 수정 취소
  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  // 입력 필드 변경 처리
  const handleInputChange = (field: string, value: string) => {
    setEditedUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // 필드 변경 핸들러 (기존 handleFieldChange를 handleInputChange로 매핑)
  const handleFieldChange = handleInputChange;
  
  // 수정 내용 저장
  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // 디버깅: editedUserInfo 전체 내용 로그
      
      // 변경된 필드 확인
      const changedFields: Record<string, boolean> = {};
      if (userInfo) {
        if (editedUserInfo.name !== userInfo.name) changedFields.name = true;
        if (editedUserInfo.phone !== userInfo.phone) changedFields.phone = true;
        if (editedUserInfo.email !== userInfo.email) changedFields.email = true;
        if (editedUserInfo.birthday !== (userInfo.birthdate || userInfo.birthday)) changedFields.birthday = true;
        if (editedUserInfo.address !== userInfo.address) changedFields.address = true;
        if (editedUserInfo.var01 !== userInfo.var01) changedFields.var01 = true;
        if (editedUserInfo.var02 !== userInfo.var02) changedFields.var02 = true;
        if (editedUserInfo.var03 !== userInfo.var03) changedFields.var03 = true;
        if (editedUserInfo.var04 !== userInfo.var04) changedFields.var04 = true;
        if (editedUserInfo.var05 !== userInfo.var05) changedFields.var05 = true;
        if (editedUserInfo.var06 !== userInfo.var06) changedFields.var06 = true;
        if (editedUserInfo.var07 !== userInfo.var07) changedFields.var07 = true;
        if (editedUserInfo.var08 !== userInfo.var08) changedFields.var08 = true;
        if (editedUserInfo.var09 !== userInfo.var09) changedFields.var09 = true;
        if (editedUserInfo.var10 !== userInfo.var10) changedFields.var10 = true;
        if (editedUserInfo.password && editedUserInfo.password.trim() !== '') changedFields.password = true;
        if (editedUserInfo.trxWithdrawAddress !== userInfo.trxWithdrawAddress) changedFields.trxWithdrawAddress = true;
        if (editedUserInfo.bscWithdrawAddress !== userInfo.bscWithdrawAddress) changedFields.bscWithdrawAddress = true;
      }
      
      
      // updateUserProfile 액션을 통해 사용자 정보 업데이트
      const resultAction = await dispatch(updateUserProfile(editedUserInfo));
      
      
      if (updateUserProfile.fulfilled.match(resultAction)) {
        // 성공적으로 업데이트된 경우
        setIsEditing(false);
        
        // 관리자 캐시 초기화 (동기)
        dispatch(clearAdminCache());
        
        // 최신 사용자 정보 다시 조회
        if (userInfo?.id) {
          const userId = userInfo.id;
          const companyId = userInfo.company_id || 21;
          dispatch(getUserById({
            userId: typeof userId === 'string' ? parseInt(userId, 10) : userId, 
            companyId 
          })).catch((error: any) => {
            console.error('[UserProfile] 사용자 정보 재조회 실패:', error);
          });
        }
        
        // 변경된 필드 하이라이트 설정
        setHighlightedFields(changedFields);
        
        alert(t('회원 정보가 성공적으로 수정되었습니다.'));
      } else {
        // 오류 처리
        if (resultAction.payload) {
          setSaveError(resultAction.payload.message || t('회원 정보 수정 중 오류가 발생했습니다.'));
        } else {
          setSaveError(t('회원 정보 수정 중 오류가 발생했습니다.'));
        }
      }
    } catch (error) {
      console.error(t('프로필 업데이트 오류:'), error);
      setSaveError(t('회원 정보 수정 중 오류가 발생했습니다.'));
    } finally {
      setIsSaving(false);
    }
  };
  
  // 프로필 업데이트 (기존 handleUpdate를 handleSave로 대체)
  const handleUpdate = handleSave;
  
  // 비밀번호 변경 토글
  const togglePasswordChange = useCallback(() => {
    setShowPasswordFields(prev => !prev);
    if (!showPasswordFields) {
      setEditedUserInfo(prev => ({ ...prev, password: '', passwordConfirm: '' }));
    }
  }, [showPasswordFields]);
  
  // 로딩 상태 타임아웃 처리 - 무한 로딩 방지
  useEffect(() => {
    if (userInfoLoading) {
      const timeout = setTimeout(() => {
        setIsInitialized(false);
        // 로딩이 너무 오래 걸리면 강제로 다시 시도
        if (!userInfo && user?.id) {
          const userId = user.id;
          const companyId = user.company_id || 21;
          dispatch(getUserById({ userId, companyId }));
        }
      }, 10000); // 10초 타임아웃

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [userInfoLoading, userInfo, user?.id, dispatch]);
  
  return {
    data: {
      userInfo,
      editedInfo: editedUserInfo,
      loading: loading || isSaving || isReloading,
      errors,
      showPasswordFields,
      isUserLoggedIn,
      isAdminMode,
      varFields,
      theme: withcookieData?.theme || {},
      isEditing,
      saveError,
      highlightedFields,
      referralLink,
      withcookieData,
      userInfoLoading
    },
    actions: {
      handleFieldChange,
      handleUpdate,
      togglePasswordChange,
      handleEdit,
      handleCancel,
      handleSave,
      copyToClipboard
    }
  };
};